import { EXPENSIVE_CSS_CLS } from '@prophecy/utils/expensiveCss';
import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import styled, { css } from 'styled-components';
import { Button } from '../Button';
import { StackItem, Stack } from '../Layout';
import { getInterFontWeightCSS, theme } from '../theme/index';
import { Box } from '../ViewBox';
import { tokens } from './tokens';
import { TabSize, TabVariant } from './types';
export const StyledTabsRoot = styled(Root)`
  height: 100%;
  display: flex;
  flex-direction: ${({ orientation })=>orientation === 'vertical' ? 'row' : 'column'};
  align-items: ${({ orientation })=>orientation === 'vertical' ? 'stretch' : 'initial'};
  gap: ${({ variant })=>variant === TabVariant.whiteTabFlat ? 0 : tokens.TabsRoot.gap};
  ${({ bordered })=>bordered ? `
    border: 1px solid ${tokens.TabsRoot.borderColor};
    border-radius: ${tokens.TabsRoot.borderRadius};
  ` : ''}
`;
export const StyledBox = styled(Box)`
  height: 100%;
  position: relative;
`;
export const tabsContainerStyle = css`
  font-family: ${tokens.TabsList.fontFamily};
  font-size: ${tokens.TabsList.fontSize};
  padding: 0 ${theme.outlineWidth};
  overflow: visible;
  ${({ variant })=>variant === TabVariant.white || variant === TabVariant.whiteTabFlat || variant === TabVariant.minimal ? `
      display: flex;
      align-items: center;
      border-radius: ${tokens.TabsList[variant].borderRadius};
      background-color: ${tokens.TabsList[variant].backgroundColor};
      border: 1px solid ${tokens.TabsList[variant].borderColor};
      padding: ${tokens.TabsList[variant].padding};
      gap: ${tokens.TabsList[variant].gap};
  ` : ''}

  ${({ orientation })=>orientation === 'vertical' && `
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: 100%;
  `}

  ${({ stretchTabs })=>stretchTabs && `
    display: flex;
  `}
`;
export const StyledTabsList = styled(List)`
  ${tabsContainerStyle}
`;
export const tabsTriggerStyle = css`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid transparent;
  transition: ${({ variant })=>variant !== TabVariant.whiteTabFlat ? tokens.TabsTrigger.transition : 'none'};

  .${EXPENSIVE_CSS_CLS} & {
    transition:
      color ${tokens.TabsTrigger.transition},
      border ${tokens.TabsTrigger.transition} !important;
  }

  /* Size & Layout */
  height: ${({ size = TabSize.m })=>tokens.TabsTrigger.size[size].height};
  padding: ${theme.spaces.x8} ${theme.spaces.x12};
  ${({ variant })=>variant === TabVariant.underline && `padding-top: 0;`}
  ${({ variant, size = TabSize.m })=>variant === TabVariant.minimal || variant === TabVariant.white ? `margin: 0;` : `margin: ${tokens.TabsTrigger.size[size].margin};`}

  /* Typography */
  ${getInterFontWeightCSS(tokens.TabsTrigger.fontWeight)};
  color: ${tokens.TabsTrigger.color};

  border-radius: ${({ variant })=>variant === TabVariant.underline || variant === TabVariant.whiteTabFlat ? '0' : tokens.TabsTrigger.borderRadius};
  ${({ variant, orientation })=>variant === TabVariant.underline && (orientation === 'vertical' ? 'border-left: 2px solid transparent;' : 'border-bottom: 2px solid transparent;')}
  ${({ variant })=>variant === TabVariant.whiteTabFlat ? `
   margin: 0; 
   font-weight: ${tokens.TabsList.whiteTabPaneFontWeight};
   border: none;
   ` : ''}


  &:focus-visible {
    box-shadow: ${theme.focusRing.shadow.skeumorphic};
  }

  &[aria-selected='true'],
  &.selected {
    background: ${({ variant = TabVariant.brand })=>tokens.TabsTrigger[variant].backgroundColor};
    color: ${({ variant = TabVariant.brand })=>tokens.TabsTrigger[variant].color};
    box-shadow: ${({ variant = TabVariant.brand })=>tokens.TabsTrigger[variant].boxShadow};

    ${({ variant = TabVariant.brand, isTabBordered, orientation })=>variant === TabVariant.whiteTabFlat ? css`
            border: 1px solid ${tokens.TabsTrigger[variant].borderColor};
            border-width: 0 1px 0;
            margin-bottom: -1px;
            margin-top: 1px;
            &:hover,
            &:focus,
            &,
            &:focus-visible {
              outline: none;
              border-radius: 0;
            }
          ` : css`
            ${variant !== TabVariant.underline && isTabBordered && `border: 1px solid ${tokens.TabsTrigger[variant].borderColor};`}

            ${variant === TabVariant.underline && `${orientation === 'vertical' ? 'border-left-color' : 'border-bottom-color'}: ${theme.colors.brand700};`}
          `}
    &:hover {
      ${({ variant })=>variant === TabVariant.gray && `background-color: ${theme.colors.gray100};`}
    }
  }

  &:hover {
    ${({ variant, orientation })=>variant === TabVariant.underline && `
        ${orientation === 'vertical' ? 'border-left-color' : 'border-bottom-color'}: ${theme.colors.brand700};
        color: ${tokens.TabsTrigger[variant].color};
      `}
    ${({ variant })=>variant === TabVariant.gray && `color: ${tokens.TabsTrigger.gray.color};`}
  }

  &[disabled] {
    cursor: not-allowed;
    color: ${tokens.TabsTrigger.disabled.color};
    box-shadow: none;
    background: ${tokens.TabsTrigger.disabled.backgroundColor};
    border: none;
    &[aria-selected='true'] {
      background: ${tokens.TabsTrigger.active.backgroundColor};
    }
  }

  ${({ orientation })=>orientation === 'vertical' && 'justify-content: flex-start;'}

  ${({ stretchTabs })=>stretchTabs && `
    flex: 1;
  `}
`;
export const StyledTabsTrigger = styled(Trigger)`
  ${tabsTriggerStyle}
  gap: 8px;
`;
export const StyledTabsContentWrap = styled(StackItem)`
  overflow: auto;
  padding: 0 ${theme.outlineWidth};
`;
export const StyledTabsContent = styled(Content)`
  height: 100%;
  outline: none;
  padding-top: ${theme.spaces.x12};
`;
export const StyledTabsTriggerGroup = styled.div`
  display: inline-flex;
  align-items: baseline;
  border-radius: ${tokens.TabsTrigger.borderRadius};
  color: ${({ variant })=>tokens.TabsTriggerGroup.TabsTrigger[variant].active.color};

  ${(props)=>props.selected && `
    background: ${tokens.TabsTrigger[props.variant].backgroundColor};
    ${StyledTabsTrigger} {
      color: ${tokens.TabsTrigger[props.variant].color};
    }
  `};

  ${StyledTabsTrigger} {
    min-width: 0;
    padding: 0 ${({ size })=>tokens.TabsTrigger.size[size].padding};
    color: ${({ variant })=>tokens.TabsTriggerGroup.TabsTrigger[variant].color};

    &[data-state='active'] {
      background-color: ${({ variant })=>tokens.TabsTriggerGroup.TabsTrigger[variant].active.backgroundColor};
      color: ${({ variant })=>tokens.TabsTriggerGroup.TabsTrigger[variant].active.color};
    }
  }
`;
export const StyledTabListContainer = styled(Stack)`
  flex-shrink: 0;
`;
export const StyledTabContainerStackItem = styled(StackItem)`
  flex-shrink: ${({ shrink })=>shrink};
`;
export const StyledTriggerGroupLabel = styled.label`
  padding-left: ${({ size })=>tokens.TabsTrigger.size[size].padding};
  padding-right: ${({ size })=>tokens.TabsTrigger.size[size].padding};
`;
export const StyledTriggerButton = styled(Button)`
  margin: 0;
`;
